import { useMemo, useState, useEffect } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { getIsValidLatitudeAndLongitude } from '../AccommodationsConsole.utils';
import { AccommodationCatalogUnitsOccupancyInput, AccommodationsConsolePropertiesV2Query, useAccommodationsConsolePropertiesV2Query } from '@graphql/generated';
import { AccommodationsConsoleState, JoyHotelAccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { useExtendedAccommodationSearchMetadata } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';
import { useEventInfo } from '@shared/utils/eventInfo';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';

interface UseAccommodationsConsoleListingsArgs {
  state: AccommodationsConsoleState;
  isInitialLoad: boolean;
  shouldPrioritizeParams?: boolean;
}

type AccommodationConsoleQueryProperty = AccommodationsConsolePropertiesV2Query['searchExtendedAccommodationProperties']['properties'][number];

type ValidAccommodationConsoleQueryProperty = Merge<
  AccommodationConsoleQueryProperty,
  { location: { latitude: number; longitude: number }; address: NonNullable<AccommodationConsoleQueryProperty['address']> }
>;

export type ConfirmedSearchPayload = Readonly<{
  location: Readonly<{
    latitude: number;
    longitude: number;
    context: {
      googlePlaceId: string | undefined;
    };
  }> | null;
  searchDates: Readonly<{
    checkin: string;
    checkout: string;
  }>;
  occupancies: AccommodationCatalogUnitsOccupancyInput;
}> | null;

const isValidProperty = (property: AccommodationConsoleQueryProperty): property is ValidAccommodationConsoleQueryProperty => {
  return isValidLatLngLocation(property.location) && !!property.address;
};

/**
 * Determines if custom search params should be skipped.
 * When true, the query uses only eventHandle (shows event venue first).
 * When false, the query includes custom search params (location, dates, occupancies).
 */
const shouldSkipCustomSearchParams = (isInitialLoad: boolean, shouldPrioritizeParams: boolean): boolean => {
  return isInitialLoad && !shouldPrioritizeParams;
};

export const useRegionalHotels = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, isInitialLoad, shouldPrioritizeParams = false } = args;
  const { eventHandle } = useEventInfo();
  const currencyCode = useTravelWhiteLabelCurrency();

  const [cachedListings, setCachedListings] = useState<Array<JoyHotelAccommodationListingDTO>>([]);
  const [confirmedSearchPayload, setConfirmedSearchPayload] = useState<ConfirmedSearchPayload>(null);

  const { isPreparingSearchMetadata, searchMetadata } = useExtendedAccommodationSearchMetadata();

  const hasValidLocationSearchCoordinates =
    !!state.circularRegionSearch && getIsValidLatitudeAndLongitude(state.circularRegionSearch.latitude, state.circularRegionSearch.longitude);

  const locationPayload = useMemo(() => {
    if (!hasValidLocationSearchCoordinates) {
      return null;
    }
    return {
      latitude: state.circularRegionSearch?.latitude,
      longitude: state.circularRegionSearch?.longitude,
      context: {
        googlePlaceId: state.circularRegionSearch?.googlePlaceId,
      },
    };
  }, [hasValidLocationSearchCoordinates, state.circularRegionSearch?.latitude, state.circularRegionSearch?.longitude, state.circularRegionSearch?.googlePlaceId]);

  const skipCustomSearchParams = shouldSkipCustomSearchParams(isInitialLoad, shouldPrioritizeParams);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  // Transform properties to JoyHotelAccommodationListingDTO format
  const transformPropertiesToListings = useMemo(
    () =>
      (properties: ReadonlyArray<ValidAccommodationConsoleQueryProperty>): Array<JoyHotelAccommodationListingDTO> => {
        return [...properties]
          .sort((a: ValidAccommodationConsoleQueryProperty, b: ValidAccommodationConsoleQueryProperty) => b.score - a.score)
          .map((listing: ValidAccommodationConsoleQueryProperty) => ({
            id: listing.id,
            type: 'standardHotel' as const,
            searchScore: listing.score,
            joyPlaceId: listing.joyPlaceId,
            isFavorite: false,
            name: listing.name,
            description: null,
            isSoldOutForSelectedDates: !!extractHotelRedirects(listing.redirectMechanisms)?.hotelSearchPageUrl,
            userNote: null,
            address: {
              street: listing.address.street,
              city: listing.address.city,
              country: listing.address.country,
            },
            location: {
              lat: listing.location.latitude,
              lng: listing.location.longitude,
            },
            starRating: listing.starRating,
            userRating: listing.userRating
              ? {
                  ratingValue: listing.userRating.ratingValue,
                  ratingCount: listing.userRating.ratingCount,
                }
              : null,
            mediaAssets: {
              isLoading: false,
              photos: listing.photos,
            },
            facilities: listing.facilities,
            pricingInfo: {
              isLoading: false,
              values: listing.pricingInfo ? mapHotelRoomPrice(listing.pricingInfo) : null,
            },
          }));
      },
    [mapHotelRoomPrice],
  );

  // Build payload conditionally - null when we can't build a valid payload
  const payload = useMemo(() => {
    if (!eventHandle) {
      return null;
    }

    // When skipping custom search params, only include eventHandle (shows event venue first)
    if (skipCustomSearchParams) {
      return {
        eventHandle,
      };
    }

    // When including custom search params, require valid location coordinates
    if (!hasValidLocationSearchCoordinates || !locationPayload || isPreparingSearchMetadata) {
      return null;
    }

    return {
      eventHandle,
      customSearchParams: {
        currencyCode,
        location: locationPayload,
        stayDates: {
          checkIn: {
            high: state.searchDates.checkin,
            low: state.searchDates.checkin,
          },
          checkOut: {
            high: state.searchDates.checkout,
            low: state.searchDates.checkout,
          },
        },
        occupancies: state.occupancies,
        metadata: searchMetadata,
      },
    };
  }, [
    eventHandle,
    skipCustomSearchParams,
    hasValidLocationSearchCoordinates,
    locationPayload,
    isPreparingSearchMetadata,
    state.searchDates.checkin,
    state.searchDates.checkout,
    state.occupancies,
    searchMetadata,
  ]);

  useEffect(() => {
    setConfirmedSearchPayload(null);
  }, [payload]);

  const {
    data,
    loading: isFetchingListings,
    error,
  } = useAccommodationsConsolePropertiesV2Query({
    variables: {
      payload: payload!,
    },
    skip: !payload,
    onCompleted: data => {
      if (data?.searchExtendedAccommodationProperties?.properties) {
        const validProperties = data.searchExtendedAccommodationProperties.properties.filter(isValidProperty);
        const tempCachedListings = transformPropertiesToListings(validProperties);
        setCachedListings(tempCachedListings as Array<JoyHotelAccommodationListingDTO>);
        const newConfirmedSearchPayload = payload?.customSearchParams
          ? {
              occupancies: payload.customSearchParams.occupancies,
              location: payload.customSearchParams.location ?? null,
              searchDates: {
                checkin: payload.customSearchParams.stayDates.checkIn.low,
                checkout: payload.customSearchParams.stayDates.checkOut.low,
              },
            }
          : null;
        setConfirmedSearchPayload(newConfirmedSearchPayload);
      }

      if (!data?.searchExtendedAccommodationProperties?.properties?.length) {
        addAction('Search extended accommodations properties with circularRegionSearch api with empty results (AccommodationsConsole)', {
          context: 'searchExtendedAccommodationProperties',
          circularRegionSearch: state.circularRegionSearch,
        });
      }
    },
    onError: error => {
      addError(error, {
        source: 'Search extended accommodations properties with circularRegionSearch api failed (AccommodationsConsole)',
        context: 'searchExtendedAccommodationProperties',
        errorMessage: error.message,
        circularRegionSearch: state.circularRegionSearch,
      });
    },
    batchMode: 'off',
  });

  const listings = useMemo((): Array<JoyHotelAccommodationListingDTO> => {
    if (!data?.searchExtendedAccommodationProperties?.properties) {
      return [];
    }
    const validProperties = data.searchExtendedAccommodationProperties.properties.filter(isValidProperty);
    return transformPropertiesToListings(validProperties);
  }, [data, transformPropertiesToListings]);

  return {
    /**
     * Can be used to retain maps points while loading new data
     */
    cachedListings,
    listings,
    isFetchingListings,
    error,
    confirmedSearchPayload,
  };
};
